<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import ActivityTimeline from 'dashboard/components-next/ActivityTimeline/ActivityTimeline.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const { funnels, loadFunnels } = useFunnelManager();

const mappings = ref([]);
const selectedMappingId = ref(null);
const isLoading = ref(false);

const selectedMapping = computed(() => {
  return mappings.value.find(m => m.id === selectedMappingId.value);
});

const loadMappings = async () => {
  isLoading.value = true;
  try {
    const response = await ConversationFunnelMappingsAPI.getMappings(
      props.conversationId
    );
    if (Array.isArray(response.data)) {
      mappings.value = response.data;
    } else if (response.data && Array.isArray(response.data.mappings)) {
      mappings.value = response.data.mappings;
    } else {
      mappings.value = [];
    }

    // Selecionar o primeiro mapping automaticamente
    if (mappings.value.length > 0 && !selectedMappingId.value) {
      selectedMappingId.value = mappings.value[0].id;
    }
  } catch {
    mappings.value = [];
  } finally {
    isLoading.value = false;
  }
};

const getFunnelIcon = funnelId => {
  const funnel = funnels.value.find(f => f.id === funnelId);
  return funnel?.settings?.icon || '📊';
};

onMounted(async () => {
  await loadFunnels();
  await loadMappings();
});

watch(
  () => props.conversationId,
  () => {
    selectedMappingId.value = null;
    loadMappings();
  }
);
</script>

<template>
  <div class="conversation-activities">
    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-4">
      <div
        class="animate-spin rounded-full h-6 w-6 border-b-2 border-woot-500"
      />
    </div>

    <!-- Empty State - Nenhum funil -->
    <div
      v-else-if="mappings.length === 0"
      class="text-center py-6 text-slate-500 dark:text-slate-400"
    >
      <fluent-icon icon="arrow-clockwise" size="32" class="mb-2" />
      <p class="text-sm">{{ t('CRM.ACTIVITY.NO_FUNNEL') }}</p>
      <p class="text-xs">{{ t('CRM.ACTIVITY.ADD_TO_FUNNEL_FIRST') }}</p>
    </div>

    <!-- Content -->
    <div v-else class="space-y-3">
      <!-- Seletor de Funil (quando há mais de um) -->
      <div v-if="mappings.length > 1" class="mb-3">
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
        >
          {{ t('CRM.ACTIVITY.SELECT_FUNNEL') }}
        </label>
        <select
          v-model="selectedMappingId"
          class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500"
        >
          <option
            v-for="mapping in mappings"
            :key="mapping.id"
            :value="mapping.id"
          >
            {{ getFunnelIcon(mapping.funnel_id) }} {{ mapping.funnel_name }} ({{
              mapping.stage_label
            }})
          </option>
        </select>
      </div>

      <!-- Funnel Badge (quando há apenas um) -->
      <div
        v-else-if="selectedMapping"
        class="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg"
      >
        <span class="text-lg">{{
          getFunnelIcon(selectedMapping.funnel_id)
        }}</span>
        <div class="flex-1">
          <p class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{ selectedMapping.funnel_name }}
          </p>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{ selectedMapping.stage_label }}
          </p>
        </div>
      </div>

      <!-- Activity Timeline -->
      <ActivityTimeline
        v-if="selectedMappingId"
        :conversation-id="props.conversationId"
        :mapping-id="selectedMappingId"
        show-add-note
        :max-items="15"
      />
    </div>
  </div>
</template>

<style scoped>
.conversation-activities {
  @apply p-4;
}
</style>
